import { PstToursAvailability, PstToursAvailabilityTimeRange } from '@/pst-tours/models/pst-tours-availability';
import { isBefore, isAfter, parseISO } from 'date-fns';
import { addMinutes } from '@/date-time/date-time-utils';

/**
 * Retrieve continuous availability range by date.
 * Assumes input is already sorted by datetime from API.
 * @param availabilities
 */
export function getContinuousAvailabilityRangesByDate(
    availabilities: Array<PstToursAvailability>
): Map<string, Array<PstToursAvailabilityTimeRange>> {
    const availabilityMap = new Map<string, Array<PstToursAvailabilityTimeRange>>();

    if (availabilities.length === 0) {
        return availabilityMap;
    }

    let currentDate: string | null = null;
    let currentRanges: Array<PstToursAvailabilityTimeRange> = [];
    let rangeStart: string | null = null;
    let rangeEnd: string | null = null;

    // Single pass through sorted data - O(n)
    for (const slot of availabilities) {
        // Skip unavailable slots
        if (!slot.is_available) continue;

        const dateKey = slot.start_datetime.split('T')[0];

        // New date detected
        if (dateKey !== currentDate) {
            // Save the last range of previous date if exists
            if (rangeStart && currentDate) {
                currentRanges.push({
                    start_datetime: rangeStart,
                    end_datetime: rangeEnd!
                });
                availabilityMap.set(currentDate, currentRanges);
            }

            // Start new date
            currentDate = dateKey;
            currentRanges = [];
            rangeStart = slot.start_datetime;
            rangeEnd = slot.end_datetime;
        } else if (slot.start_datetime <= rangeEnd!) { // Same date - check if slot overlaps or is adjacent
            // Extend range if this slot ends later
            if (slot.end_datetime > rangeEnd!) {
                rangeEnd = slot.end_datetime;
            }
        } else { // Gap detected - save current range and start new one
            currentRanges.push({
                start_datetime: rangeStart!,
                end_datetime: rangeEnd!
            });
            rangeStart = slot.start_datetime;
            rangeEnd = slot.end_datetime;
        }
    }

    // Save the final range
    if (rangeStart && currentDate) {
        currentRanges.push({
            start_datetime: rangeStart,
            end_datetime: rangeEnd!
        });
        availabilityMap.set(currentDate, currentRanges);
    }

    return availabilityMap;
}

/**
 * Check if a tour fits completely within any of the availability ranges.
 * @param availabilityRanges Array of availability time ranges
 * @param startDateTime ISO datetime string for tour start
 * @param duration Duration in minutes
 */
export function isTourInRange(
    availabilityRanges: Array<PstToursAvailabilityTimeRange>,
    startDateTime: string,
    duration: number
): boolean {
    const tourEndDateTime = addMinutes(startDateTime, duration);

    // Parse dates once for accurate comparison
    const tourStart = parseISO(startDateTime);
    const tourEnd = parseISO(tourEndDateTime);

    // Check each availability range
    for (const range of availabilityRanges) {
        const rangeStart = parseISO(range.start_datetime);
        const rangeEnd = parseISO(range.end_datetime);

        // Tour fits if: start >= rangeStart AND end <= rangeEnd
        if (!isBefore(tourStart, rangeStart) && !isAfter(tourEnd, rangeEnd)) {
            return true;
        }
    }

    return false;
}
